import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Collapse,
  Alert,
} from 'reactstrap';
import FileUpload from '../../Common/FileUpload';
import { Form, Input } from '@availity/form';
import Select from '@availity/select';
import { Switch } from '../../../lib/form-field';
import '@availity/yup';
import formValidation from './formValidation';
import TreeSelector from '../../Common/TreeSelector';
import FormErrorMessage from '../../Common/FormErrorMessage';
import * as weeklyPushNotificationsActions from '../../../store/pushNotifications/weekly/actions';
import * as countriesActions from '../../../store/countries/actions';
import { useDispatch, useSelector } from 'react-redux';
import { SubmitButton } from '../../Common/Modal/modalActionButtons';
import { isEmptyObj } from '../../../helpers/sharedFunction';
import timeTags from '../../../config/timeTags';
import PushNotificationPreview from '../PushNotificationPreview';

const FormWeeklyPushNotification = ({
  weeklyPushNotification,
  loading,
  create,
  closeModal,
}) => {
  const [files, setFiles] = useState(null);
  const [errorFile, setErrorFile] = useState('');
  const [lang, setLang] = useState(weeklyPushNotification?.lang || 'it');
  const [tags, setTags] = useState(weeklyPushNotification?.timeTags || []);

  const countries = useSelector((state) => state.Country.countries);

  const tagsLabel = tags.length ? `(${tags.slice(0, 4).join(', ')}, ...)` : '';

  const handleFiles = (name, files) => {
    setFiles((state) => ({ ...state, [name]: files[0] }));
    setErrorFile('');
  };

  const handleRemoveFiles = (name) => {
    let newFiles = { ...files };
    delete newFiles[name];
    setFiles(isEmptyObj(newFiles) ? null : newFiles);
  };

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(countriesActions.syncCountries());
  }, []);

  const handleLangChange = (value, setFieldValue) => {
    setFieldValue('lang', value, false);
    setLang(value);
  };

  const handleSubmit = (values) => {
    if (!tags.length)
      return setErrorFile('Insert at least one time tag, please!');
    if (!weeklyPushNotification?.image && !files?.image)
      return setErrorFile('Insert image, please!');

    // Ensure name equals title for weekly push notifications
    values.name = values.title;

    dispatch(
      create
        ? weeklyPushNotificationsActions.createWeeklyPushNotification({
            ...values,
            files,
            timeTags: tags,
            lang,
          })
        : weeklyPushNotificationsActions.updateWeeklyPushNotification({
            id: weeklyPushNotification.id,
            ...values,
            ...(files && { files }),
            timeTags: tags,
            lang,
          }),
    );
    closeModal();
  };

  return (
    <Card>
      <CardBody>
        <Form
          initialValues={{
            ...weeklyPushNotification,
            lang,
          }}
          validationSchema={formValidation}
          onSubmit={handleSubmit}>
          {({ isValid, values, setFieldValue }) => {
            return (
              <>
                <Collapse isOpen={!!errorFile}>
                  <Alert color="danger">{errorFile}</Alert>
                </Collapse>
                <Row>
                  <Col md="8">
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="name">Name</Label>
                          <Input type="text" name="name" id="name" />
                          <FormErrorMessage name="name" />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label htmlFor="lang">Language</Label>
                          <Select
                            name="lang"
                            id="lang"
                            isSearchable
                            onChange={(value) =>
                              handleLangChange(value, setFieldValue)
                            }
                            options={countries.map(({ name, lang }) => ({
                              label: name,
                              value: lang,
                            }))}
                          />
                          <FormErrorMessage name="lang" />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="timeTags">
                            Time Tags{' '}
                            <span className="font-size-11 text-muted truncate truncate-2">
                              {tagsLabel}
                            </span>
                          </Label>
                          <TreeSelector
                            tree={timeTags}
                            selected={weeklyPushNotification?.timeTags}
                            onCheck={(checked) => setTags(checked)}
                            checkModel="leaf"
                            singleLeafSelection
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="dayOfWeek">Day of Week</Label>
                          <Select
                            name="dayOfWeek"
                            id="dayOfWeek"
                            options={Array.from({ length: 7 }, (_, i) => ({
                              label: `Day ${i}`,
                              value: i,
                            }))}
                          />
                          <FormErrorMessage name="dayOfWeek" />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="title">Title</Label>
                          <Input type="text" name="title" id="title" />
                          <FormErrorMessage name="title" />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="message">Message</Label>
                          <Input type="textarea" name="message" id="message" />
                          <FormErrorMessage name="message" />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label htmlFor="deeplink">Deeplink</Label>
                          <Input
                            type="textarea"
                            name="deeplink"
                            id="deeplink"
                          />
                          <FormErrorMessage name="deeplink" />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="4">
                        <FormGroup>
                          <Label htmlFor="image">Image</Label>
                          <FileUpload
                            name="image"
                            accept={{
                              'image/*': ['.png', '.jpeg', '.jpg'],
                            }}
                            maxFiles={1}
                            uploadMessage="Drop image here or click to upload."
                            handleFiles={handleFiles}
                            handleRemoveFiles={handleRemoveFiles}
                            showPreview
                            existingFile={weeklyPushNotification?.image}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Switch
                            name="active"
                            label="Active?"
                            defaultChecked={
                              weeklyPushNotification?.active !== false
                            }
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </Col>
                  <Col md="4">
                    <div style={{ position: 'sticky', top: '0' }}>
                      <PushNotificationPreview
                        title={values?.title}
                        message={values?.message}
                        image={
                          files?.image?.preview || weeklyPushNotification?.image
                        }
                      />
                    </div>
                  </Col>
                </Row>
                <div className="modal-footer">
                  <SubmitButton
                    disabled={!isValid || loading}
                    create={create}
                  />
                </div>
              </>
            );
          }}
        </Form>
      </CardBody>
    </Card>
  );
};

export default FormWeeklyPushNotification;
